/**
 * Phones AI parity (feat/phones-ai-parity): what Atlas and Pulse Point do
 * with call recordings, for every dental office.
 *
 * - The near-real-time callvault feed (`callvault.recordings.v2`): one item
 *   per new 3CX recording or voicemail, keyed by its 3CX path (the CDR feed
 *   already tied that path to the call), with the transcript and the BAA
 *   model's analysis.
 * - The AI block on a call (reason, outcome, caller and staff tone, numeric
 *   sentiment, QA scorecard, coaching tip, follow-ups, missed opportunity).
 * - The voicemail inbox (status, assignee, callback).
 * - Transcript and summary search.
 * - Per-office and per-agent AI insights (Atlas store grades and coaching,
 *   Pulse Point's insight overview).
 *
 * PHI: transcripts, summaries, opportunity notes, coaching tips and search
 * snippets are PHI. They are returned only to a user holding `patient.view`
 * at the call's office, and every read is audited. Scores, tones, reasons
 * and counts carry no identifiers.
 *
 * This file imports only zod so phones.ts and call-analytics.ts can both use it.
 */
import { z } from 'zod';

const Uuid = z.uuid();
const Iso = z.iso.datetime({ offset: true });
const Count = z.number().int().nonnegative();

/* ------------------------------------------------------------------------ */
/* Vocabulary (Atlas callAnalyze.js tones; Pulse Point outcome gaps)         */
/* ------------------------------------------------------------------------ */

export const CALLER_TONES = [
  'calm',
  'friendly',
  'frustrated',
  'angry',
  'confused',
  'rushed',
  'upset',
  'anxious',
  'neutral',
] as const;
export const CallerToneSchema = z.enum(CALLER_TONES);
export type CallerTone = z.infer<typeof CallerToneSchema>;
/** Atlas gradeStore: these caller tones count as a rough call. */
export const ROUGH_CALLER_TONES: readonly CallerTone[] = ['frustrated', 'angry', 'upset'];

export const AGENT_TONES = [
  'helpful',
  'professional',
  'friendly',
  'calm',
  'apologetic',
  'rushed',
  'dismissive',
  'abrupt',
  'neutral',
] as const;
export const AgentToneSchema = z.enum(AGENT_TONES);
export type AgentTone = z.infer<typeof AgentToneSchema>;
/** Atlas gradeStore "How we sounded": these staff tones are good. */
export const GOOD_AGENT_TONES: readonly AgentTone[] = [
  'helpful',
  'professional',
  'friendly',
  'calm',
  'apologetic',
];

export const CALL_AI_OUTCOMES = [
  'booked',
  'rescheduled',
  'cancelled',
  'not_booked',
  'info_given',
  'message_taken',
  'transferred',
  'no_action',
] as const;
export const CallAiOutcomeSchema = z.enum(CALL_AI_OUTCOMES);
export type CallAiOutcome = z.infer<typeof CallAiOutcomeSchema>;
export const CALL_AI_OUTCOME_LABEL: Record<CallAiOutcome, string> = {
  booked: 'Booked',
  rescheduled: 'Rescheduled',
  cancelled: 'Cancelled',
  not_booked: 'Not booked',
  info_given: 'Information given',
  message_taken: 'Message taken',
  transferred: 'Transferred',
  no_action: 'No action',
};

export const URGENCIES = ['low', 'normal', 'urgent'] as const;
export const UrgencySchema = z.enum(URGENCIES);
export type Urgency = z.infer<typeof UrgencySchema>;

/** Per-call QA scorecard: each part 0..5 (null when the call gave no chance to show it). */
export const QA_PARTS = ['greeting', 'listening', 'resolution', 'booking', 'closing'] as const;
export type QaPart = (typeof QA_PARTS)[number];
export const QA_PART_LABEL: Record<QaPart, string> = {
  greeting: 'Greeting',
  listening: 'Listened and understood',
  resolution: 'Resolved the need',
  booking: 'Offered or made a booking',
  closing: 'Clear close and next step',
};
const QaValue = z.number().int().min(0).max(5).nullable();
export const QaScorecardSchema = z.object({
  greeting: QaValue,
  listening: QaValue,
  resolution: QaValue,
  booking: QaValue,
  closing: QaValue,
});
export type QaScorecard = z.infer<typeof QaScorecardSchema>;

/** 0..100 from the parts that apply (null when none do). Same formula on both sides. */
export function qaScoreOf(qa: QaScorecard | null | undefined): number | null {
  if (!qa) return null;
  const vals = QA_PARTS.map((k) => qa[k]).filter((v): v is number => typeof v === 'number');
  if (vals.length === 0) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / (vals.length * 5)) * 100);
}

/** Atlas letter grades (callStats.gradeStore). */
export function letterGrade(score: number): string {
  if (score >= 93) return 'A';
  if (score >= 85) return 'A-';
  if (score >= 78) return 'B+';
  if (score >= 70) return 'B';
  if (score >= 62) return 'C+';
  if (score >= 55) return 'C';
  if (score >= 45) return 'D';
  return 'F';
}

/* ------------------------------------------------------------------------ */
/* Feed v2: callvault -> Velora, every few minutes                           */
/* ------------------------------------------------------------------------ */

export const CALLVAULT_SCHEMA_V2 = 'callvault.recordings.v2';
export const CALLVAULT_MAX_ITEMS_V2 = 200;

/**
 * A 3CX path relative to the PBX's Recordings (or Voicemail) folder, as the
 * CDR (`cdrrecordings.recording_url`) has it: `<ext>/<file>.wav`. It never
 * reaches a browser; Velora uses it to find the call and to ask callvault
 * for the audio.
 */
export const VaultPathSchema = z
  .string()
  .min(3)
  .max(300)
  .refine((p) => !p.startsWith('/') && !p.includes('..') && !/^[a-z]+:/i.test(p), {
    message: 'expected a relative 3CX path',
  })
  // eslint-disable-next-line no-control-regex
  .refine((p) => !/[\u0000-\u001f]/.test(p), { message: 'control characters' });

export const TranscriptInSchema = z.object({
  /** Whisper engine id, e.g. `whisper-large-v3-turbo@c2g`. */
  engine: z.string().trim().min(1).max(80),
  language: z.string().trim().min(2).max(12).nullable().default(null),
  text: z.string().max(60_000),
  /** Whisper chunks: start offset and text (no speaker: recordings are mono). */
  segments: z
    .array(z.object({ startMs: z.number().int().min(0), text: z.string().max(8_000) }))
    .max(400)
    .default([]),
});
export type TranscriptIn = z.infer<typeof TranscriptInSchema>;

export const CallvaultAnalysisSchema = z.object({
  minedAt: Iso,
  model: z.string().trim().min(1).max(80).nullable().default(null),
  relevant: z.boolean(),
  /** One of CALL_REASONS (call-analytics.ts); unknown values become `other`. */
  category: z.string().trim().min(1).max(40),
  newPatient: z.boolean().nullable().default(null),
  booked: z.boolean().nullable().default(null),
  outcome: CallAiOutcomeSchema.nullable().default(null),
  sentiment: z.enum(['positive', 'neutral', 'negative']).nullable().default(null),
  sentimentScore: z.number().min(-1).max(1).nullable().default(null),
  callerTone: CallerToneSchema.nullable().default(null),
  agentTone: AgentToneSchema.nullable().default(null),
  needsFollowUp: z.boolean().nullable().default(null),
  missedOpportunity: z.boolean().nullable().default(null),
  opportunityNote: z.string().trim().max(300).nullable().default(null),
  summary: z.string().trim().max(2000).nullable().default(null),
  followUps: z.array(z.string().trim().min(1).max(200)).max(5).default([]),
  qa: QaScorecardSchema.nullable().default(null),
  coachingTip: z.string().trim().max(300).nullable().default(null),
  /** Voicemail triage. */
  urgency: UrgencySchema.nullable().default(null),
  callbackRequested: z.boolean().nullable().default(null),
});
export type CallvaultAnalysis = z.infer<typeof CallvaultAnalysisSchema>;

export const CallvaultRecordingItemSchema = z.object({
  kind: z.enum(['call', 'voicemail']),
  path: VaultPathSchema,
  /** When the audio was recorded (file time); used to find the call. */
  recordedAt: Iso,
  /** Extension folder the file sits in. */
  extension: z.string().regex(/^[0-9A-Za-z*#]{1,16}$/),
  seconds: z.number().int().min(0).max(86_400).nullable().default(null),
  /** Voicemail only: the caller's number from the file name, to pick the right call. */
  callerDigits: z
    .string()
    .regex(/^[0-9]{7,15}$/)
    .nullable()
    .default(null),
  transcript: TranscriptInSchema.nullable().default(null),
  analysis: CallvaultAnalysisSchema.nullable().default(null),
});
export type CallvaultRecordingItem = z.infer<typeof CallvaultRecordingItemSchema>;

export const CallvaultRecordingsBatchSchema = z.object({
  schema: z.literal(CALLVAULT_SCHEMA_V2),
  batchId: z.string().regex(/^[A-Za-z0-9._:-]{8,100}$/),
  office: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,39}$/),
  generatedAt: Iso,
  items: z.array(CallvaultRecordingItemSchema).max(CALLVAULT_MAX_ITEMS_V2),
});
export type CallvaultRecordingsBatch = z.infer<typeof CallvaultRecordingsBatchSchema>;

export const CallvaultRecordingsResultSchema = z.object({
  batchId: z.string(),
  received: Count,
  /** Items tied to a call and stored. */
  applied: Count,
  /** Items whose stored transcript or analysis did not change. */
  unchanged: Count,
  transcripts: Count,
  voicemails: Count,
  /**
   * Items not applied, by a short hash of the path (never the path: it holds
   * the caller's number). `call_not_found` is retried by callvault: the CDR
   * feed may not have delivered the call yet.
   */
  rejected: z.array(z.object({ ref: z.string(), reason: z.string() })),
  duplicate: z.boolean(),
});
export type CallvaultRecordingsResult = z.infer<typeof CallvaultRecordingsResultSchema>;

/* ------------------------------------------------------------------------ */
/* The AI block on a call                                                    */
/* ------------------------------------------------------------------------ */

export const CallAiSchema = z.object({
  kind: z.enum(['call', 'voicemail']),
  reason: z.string(),
  reasonLabel: z.string(),
  relevant: z.boolean(),
  outcome: CallAiOutcomeSchema.nullable(),
  newPatient: z.boolean().nullable(),
  bookedOnCall: z.boolean().nullable(),
  sentiment: z.enum(['positive', 'neutral', 'negative']).nullable(),
  sentimentScore: z.number().nullable(),
  callerTone: CallerToneSchema.nullable(),
  agentTone: AgentToneSchema.nullable(),
  needsFollowUp: z.boolean().nullable(),
  missedOpportunity: z.boolean().nullable(),
  qa: QaScorecardSchema.nullable(),
  qaScore: z.number().int().min(0).max(100).nullable(),
  urgency: UrgencySchema.nullable(),
  callbackRequested: z.boolean().nullable(),
  model: z.string().nullable(),
  minedAt: Iso.nullable(),
  /** PHI; null unless the viewer holds patient.view at the call's office. */
  text: z
    .object({
      summary: z.string().nullable(),
      opportunityNote: z.string().nullable(),
      followUps: z.array(z.string()),
      coachingTip: z.string().nullable(),
    })
    .nullable(),
});
export type CallAi = z.infer<typeof CallAiSchema>;

/* ------------------------------------------------------------------------ */
/* Voicemail inbox                                                           */
/* ------------------------------------------------------------------------ */

export const VOICEMAIL_STATUSES = ['new', 'assigned', 'called_back', 'closed'] as const;
export const VoicemailStatusSchema = z.enum(VOICEMAIL_STATUSES);
export type VoicemailStatus = z.infer<typeof VoicemailStatusSchema>;
export const VOICEMAIL_STATUS_LABEL: Record<VoicemailStatus, string> = {
  new: 'New',
  assigned: 'Assigned',
  called_back: 'Called back',
  closed: 'Closed',
};

export const VoicemailListQuerySchema = z.object({
  status: z.enum(['open', 'all', ...VOICEMAIL_STATUSES]).default('open'),
  days: z.coerce.number().int().min(1).max(90).default(14),
  mine: z.coerce.boolean().default(false),
});
export type VoicemailListQuery = z.infer<typeof VoicemailListQuerySchema>;

export const VoicemailItemSchema = z.object({
  callId: Uuid,
  locationId: Uuid,
  officeName: z.string(),
  at: Iso,
  /** Display name or formatted number (PHI); masked by the client in privacy mode. */
  caller: z.string(),
  patientId: Uuid.nullable(),
  /** The caller's number (E.164) for Call back; null when withheld. */
  number: z.string().nullable(),
  lineLabel: z.string().nullable(),
  /** Audio is in callvault and can be played. */
  hasAudio: z.boolean(),
  seconds: z.number().int().nonnegative().nullable(),
  status: VoicemailStatusSchema,
  assignedUserId: Uuid.nullable(),
  assignedName: z.string().nullable(),
  handledAt: Iso.nullable(),
  handledByName: z.string().nullable(),
  /** The caller was reached later (an answered call to or from the number after the message). */
  reachedSince: z.boolean(),
  urgency: UrgencySchema.nullable(),
  callbackRequested: z.boolean().nullable(),
  reason: z.string().nullable(),
  reasonLabel: z.string().nullable(),
  /** PHI: null without patient.view, or until callvault transcribed it. */
  transcript: z.string().nullable(),
  summary: z.string().nullable(),
  /** Why no transcript ("Voicemail audio not connected for this office yet", ...). */
  transcriptNote: z.string().nullable(),
  /**
   * Seconds the caller spent in the voicemail box (greeting plus message), from
   * the 3CX CDR voicemail leg. Null when the PBX sent no voicemail leg.
   */
  boxSeconds: z.number().int().nonnegative().nullable(),
  /** This office's voicemail audio reaches Velora (any voicemail of the office has audio). */
  officeAudioConnected: z.boolean(),
});
export type VoicemailItem = z.infer<typeof VoicemailItemSchema>;

export const VoicemailListResponseSchema = z.object({
  items: z.array(VoicemailItemSchema),
  counts: z.object({ new: Count, assigned: Count, calledBack: Count, closed: Count }),
  /** False until callvault sends voicemail audio for any office in scope. */
  audioConnected: z.boolean(),
  assignees: z.array(z.object({ userId: Uuid, name: z.string() })),
});
export type VoicemailListResponse = z.infer<typeof VoicemailListResponseSchema>;

export const VoicemailActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('assign'), userId: Uuid.nullable() }),
  z.object({
    action: z.literal('status'),
    status: z.enum(['new', 'called_back', 'closed']),
    note: z.string().trim().max(1000).optional(),
  }),
]);
export type VoicemailAction = z.infer<typeof VoicemailActionSchema>;

/* ------------------------------------------------------------------------ */
/* Search                                                                    */
/* ------------------------------------------------------------------------ */

export const CallSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(200),
  days: z.coerce.number().int().min(1).max(365).default(90),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type CallSearchQuery = z.infer<typeof CallSearchQuerySchema>;

export const CallSearchHitSchema = z.object({
  callId: Uuid,
  locationId: Uuid,
  officeName: z.string(),
  startedAt: Iso,
  direction: z.enum(['inbound', 'outbound', 'internal']),
  disposition: z.string(),
  caller: z.string(),
  patientId: Uuid.nullable(),
  reasonLabel: z.string().nullable(),
  matchedIn: z.enum(['transcript', 'summary', 'voicemail']),
  /** PHI: a short excerpt with the matched words between « and ». */
  snippet: z.string(),
  rank: z.number(),
});
export type CallSearchHit = z.infer<typeof CallSearchHitSchema>;

export const CallSearchResponseSchema = z.object({
  hits: z.array(CallSearchHitSchema),
  /** Offices in scope the viewer may not read transcripts at (searched nothing there). */
  skippedOffices: z.number().int().nonnegative(),
});
export type CallSearchResponse = z.infer<typeof CallSearchResponseSchema>;

/* ------------------------------------------------------------------------ */
/* AI insights per office and per agent                                      */
/* ------------------------------------------------------------------------ */

export const AiInsightsQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).default(30),
});

const Share = z.object({ key: z.string(), label: z.string(), count: Count });

export const AiGradePartSchema = z.object({
  key: z.enum(['answering', 'followup', 'manner', 'outcome', 'quality']),
  label: z.string(),
  score: z.number().int().min(0).max(100).nullable(),
  weight: z.number().int().positive(),
  detail: z.string(),
});
export type AiGradePart = z.infer<typeof AiGradePartSchema>;

export const AiAgentRowSchema = z.object({
  extension: z.string(),
  name: z.string().nullable(),
  analysed: Count,
  qaScore: z.number().int().min(0).max(100).nullable(),
  goodManner: Count,
  roughCalls: Count,
  booked: Count,
  /** Of `booked`: an appointment for the caller shows in the schedule (see AiBookingCheckSchema). */
  bookedConfirmed: Count,
  missedOpportunities: Count,
  /** Weakest QA part on average, for coaching. */
  weakest: z.string().nullable(),
});
export type AiAgentRow = z.infer<typeof AiAgentRowSchema>;

/**
 * A coaching line with what it counts, so the screen can link it to the calls
 * behind it (the `coaching` strings stay for older clients). Counts only.
 */
export const AI_ACTION_KINDS = [
  'rough_callers',
  'missed_opportunity',
  'new_patients',
  'voicemails',
  'weak_qa_part',
  'desk_qa',
] as const;
export const AiActionKindSchema = z.enum(AI_ACTION_KINDS);
export type AiActionKind = z.infer<typeof AiActionKindSchema>;
export const AiActionSchema = z.object({
  kind: AiActionKindSchema,
  /** Same sentence as the matching `coaching` line. */
  text: z.string(),
  /** How many calls (or voicemails) it is about; 0 when it is an average. */
  count: Count,
  /** `bad`: fix first; `watch`: worth a look. */
  severity: z.enum(['bad', 'watch']),
  /** missed_opportunity: the reason most of them were about. */
  reason: z.string().nullable(),
  /** desk_qa: the extension. */
  extension: z.string().nullable(),
});
export type AiAction = z.infer<typeof AiActionSchema>;

/** The same grade for the window just before (trend); null when it had no calls. */
export const AiPreviousPeriodSchema = z.object({
  calls: Count,
  analysed: Count,
  score: z.number().int().min(0).max(100).nullable(),
  parts: z.array(
    z.object({
      key: AiGradePartSchema.shape.key,
      score: z.number().int().min(0).max(100).nullable(),
    }),
  ),
  qaScore: z.number().int().min(0).max(100).nullable(),
  newPatientCalls: Count,
  newPatientBooked: Count,
  missedOpportunities: Count,
});
export type AiPreviousPeriod = z.infer<typeof AiPreviousPeriodSchema>;

/**
 * The AI's "booked" next to the schedule, never one silently (audit D14).
 * `claimed`: reviewed calls the AI marked booked. Each is checked against
 * the appointments of every patient the caller's number belongs to (the
 * call's patient, its contact, any patient or family member with that phone):
 * - `confirmed`: an appointment was created for one of them from 5 minutes
 *   before the call to 24 hours after it (or, for a reschedule, an upcoming
 *   appointment was changed in that window), or staff linked one to the call;
 * - `notFound`: the caller is a known patient but no such appointment exists;
 * - `noPatient`: the number matches no patient, so it cannot be checked.
 */
export const AiBookingCheckSchema = z.object({
  claimed: Count,
  confirmed: Count,
  notFound: Count,
  noPatient: Count,
});
export type AiBookingCheck = z.infer<typeof AiBookingCheckSchema>;

export const AiOfficeInsightsSchema = z.object({
  locationId: Uuid,
  officeName: z.string(),
  calls: Count,
  analysed: Count,
  relevant: Count,
  /** Store grade (Atlas rubric + QA); null under 10 inbound calls. */
  grade: z
    .object({
      score: z.number().int().min(0).max(100),
      letter: z.string(),
      parts: z.array(AiGradePartSchema),
      weakest: z.string().nullable(),
    })
    .nullable(),
  reasons: z.array(Share),
  outcomes: z.array(Share),
  sentiment: z.object({ positive: Count, neutral: Count, negative: Count }),
  avgSentimentScore: z.number().nullable(),
  callerTones: z.array(Share),
  agentTones: z.array(Share),
  needsFollowUp: Count,
  missedOpportunities: Count,
  newPatientCalls: Count,
  newPatientBooked: Count,
  /** The AI's "booked" calls checked against the schedule. */
  bookings: AiBookingCheckSchema,
  qaScore: z.number().int().min(0).max(100).nullable(),
  qaParts: z.array(z.object({ key: z.string(), label: z.string(), avg: z.number().nullable() })),
  agents: z.array(AiAgentRowSchema),
  voicemails: z.object({ total: Count, open: Count, urgent: Count, transcribed: Count }),
  /** Plain-language coaching lines built from the counts (no PHI). */
  coaching: z.array(z.string()),
  /** The coaching lines, structured, most urgent first. */
  actions: z.array(AiActionSchema),
  /** The previous window of the same length, for trends. */
  previous: AiPreviousPeriodSchema.nullable(),
});
export type AiOfficeInsights = z.infer<typeof AiOfficeInsightsSchema>;

export const AiInsightsResponseSchema = z.object({
  days: z.number().int(),
  generatedAt: Iso,
  offices: z.array(AiOfficeInsightsSchema),
  /** Newest analysis received per office (pipeline freshness). */
  freshness: z.array(z.object({ locationId: Uuid, lastMinedAt: Iso.nullable() })),
});
export type AiInsightsResponse = z.infer<typeof AiInsightsResponseSchema>;
